import React, { useCallback, useEffect, useRef, useState } from "react";
import HtmlEditor, { Toolbar, Item } from "devextreme-react/html-editor";
import "devextreme/dist/css/dx.light.css";
import {
	getLegalDocument,
	updateLegalDocument,
	publishLegalDocument,
	getLegalHistory,
	getLegalHistoryEntry,
	rollbackLegalDocument,
} from "../../api/apiServiceFetch";
import "./Documents.scss";

const DOC_TYPES = [
	{ value: "terms", label: "Пользовательское соглашение" },
	{ value: "privacy", label: "Политика конфиденциальности" },
];

const LANGS = [
	{ value: "ru", label: "Русский" },
	{ value: "en", label: "Английский" },
	{ value: "hy", label: "Армянский" },
	{ value: "fr", label: "Французский" },
];

const HEADER_VALUES = [false, 2, 3];

const Documents = () => {
	const [docType, setDocType] = useState("terms");
	const [lang, setLang] = useState("ru");
	const [title, setTitle] = useState("");
	const [effectiveDate, setEffectiveDate] = useState("");
	const [bodyHtml, setBodyHtml] = useState("");
	const [saved, setSaved] = useState({ title: "", effectiveDate: "", bodyHtml: "" });
	const [isLoading, setIsLoading] = useState(true);
	const [isSaving, setIsSaving] = useState(false);
	const [status, setStatus] = useState(null); // { ok: bool, text: string }
	const [currentVersion, setCurrentVersion] = useState(null);
	const [history, setHistory] = useState([]);
	const [viewEntry, setViewEntry] = useState(null); // LegalHistoryDetailItem
	const loadSeqRef = useRef(0);
	const historySeqRef = useRef(0);

	const isDirty =
		title !== saved.title ||
		effectiveDate !== saved.effectiveDate ||
		bodyHtml !== saved.bodyHtml;

	const load = useCallback(async (nextDocType, nextLang) => {
		const seq = ++loadSeqRef.current;
		setIsLoading(true);
		setStatus(null);
		try {
			const data = await getLegalDocument(nextDocType, nextLang);
			if (seq !== loadSeqRef.current) return;
			const loaded = {
				title: data?.title || "",
				effectiveDate: data?.effectiveDate || "",
				bodyHtml: data?.bodyHtml || "",
			};
			setTitle(loaded.title);
			setEffectiveDate(loaded.effectiveDate);
			setBodyHtml(loaded.bodyHtml);
			setSaved(loaded);
		} catch (error) {
			if (seq !== loadSeqRef.current) return;
			console.error("Load legal document error:", error);
			setStatus({ ok: false, text: "Не удалось загрузить документ" });
		} finally {
			if (seq === loadSeqRef.current) setIsLoading(false);
		}
	}, []);

	const reloadHistory = useCallback(async () => {
		const seq = ++historySeqRef.current;
		try {
			const items = await getLegalHistory(docType);
			if (seq !== historySeqRef.current) return;
			setHistory(items || []);
			setCurrentVersion(
				items && items.length ? Math.max(...items.map((h) => h.version)) : 1,
			);
		} catch (error) {
			if (seq !== historySeqRef.current) return;
			console.error("Load legal history error:", error);
		}
	}, [docType]);

	useEffect(() => {
		load(docType, lang);
		reloadHistory();
	}, [docType, lang, load, reloadHistory]);

	const confirmDiscard = () =>
		!isDirty ||
		window.confirm("Есть несохранённые изменения. Продолжить без сохранения?");

	const handleDocTypeChange = (value) => {
		if (!confirmDiscard()) return;
		setViewEntry(null);
		setDocType(value);
	};

	const handleLangChange = (value) => {
		if (!confirmDiscard()) return;
		setViewEntry(null);
		setLang(value);
	};

	const handleSave = async () => {
		if (
			!window.confirm(
				"Сохранить правку текущей версии? Текст изменится для всех, повторное согласие у пользователей запрошено НЕ будет.",
			)
		) {
			return;
		}
		setIsSaving(true);
		setStatus(null);
		try {
			const data = await updateLegalDocument(docType, lang, {
				title,
				bodyHtml,
				effectiveDate,
			});
			const persisted = {
				title: data?.title || "",
				effectiveDate: data?.effectiveDate || "",
				bodyHtml: data?.bodyHtml || "",
			};
			setTitle(persisted.title);
			setEffectiveDate(persisted.effectiveDate);
			setBodyHtml(persisted.bodyHtml);
			setSaved(persisted);
			setStatus({ ok: true, text: "Сохранено" });
		} catch (error) {
			console.error("Save legal document error:", error);
			setStatus({ ok: false, text: "Не удалось сохранить документ" });
		} finally {
			setIsSaving(false);
		}
	};

	const handlePublish = async () => {
		if (
			!window.confirm(
				"Опубликовать новую версию документа? ВСЕ пользователи должны будут заново принять условия при следующем входе.",
			)
		) {
			return;
		}
		setIsSaving(true);
		setStatus(null);
		try {
			const data = await publishLegalDocument(docType);
			setCurrentVersion(data?.version ?? null);
			await reloadHistory();
			setStatus({ ok: true, text: `Опубликована версия ${data?.version}` });
		} catch (error) {
			console.error("Publish legal document error:", error);
			setStatus({ ok: false, text: "Не удалось опубликовать версию" });
		} finally {
			setIsSaving(false);
		}
	};

	const openEntry = async (id) => {
		try {
			setViewEntry(await getLegalHistoryEntry(id));
		} catch (error) {
			console.error("Load history entry error:", error);
			setStatus({ ok: false, text: "Не удалось загрузить версию" });
		}
	};

	const handleRollback = async (version) => {
		if (
			!window.confirm(
				`Откатить документ к версии ${version}? Старый текст будет опубликован как НОВАЯ версия, и все пользователи должны будут заново принять условия.`,
			)
		) {
			return;
		}
		setIsSaving(true);
		let rolledBack = false;
		try {
			const data = await rollbackLegalDocument(docType, version);
			setStatus({ ok: true, text: `Откат выполнен, новая версия ${data?.version}` });
			setCurrentVersion(data?.version ?? null);
			rolledBack = true;
		} catch (error) {
			console.error("Rollback legal document error:", error);
			setStatus({ ok: false, text: "Не удалось выполнить откат" });
		} finally {
			setIsSaving(false);
		}
		if (!rolledBack) return;
		try {
			await Promise.all([load(docType, lang), reloadHistory()]);
		} catch (error) {
			console.error("Reload after rollback error:", error);
			setStatus({
				ok: false,
				text: "Откат выполнен, но не удалось обновить данные — обновите страницу",
			});
		}
	};

	return (
		<div className="documents-page">
			<h2>Документы</h2>
			<section className="documents-page__card">
				<p className="documents-page__hint">
					Пользовательское соглашение и политика конфиденциальности. Текст
					берётся из базы и отображается в мобильном приложении, веб-версии и
					на лендинге сразу после сохранения.
				</p>
				<div className="documents-page__row">
					<select
						value={docType}
						disabled={isLoading || isSaving}
						onChange={(e) => handleDocTypeChange(e.target.value)}
					>
						{DOC_TYPES.map((d) => (
							<option key={d.value} value={d.value}>
								{d.label}
							</option>
						))}
					</select>
					<select
						value={lang}
						disabled={isLoading || isSaving}
						onChange={(e) => handleLangChange(e.target.value)}
					>
						{LANGS.map((l) => (
							<option key={l.value} value={l.value}>
								{l.label}
							</option>
						))}
					</select>
				</div>
				<div className="documents-page__field">
					<label>Заголовок</label>
					<input
						type="text"
						value={title}
						disabled={isLoading || isSaving}
						onChange={(e) => setTitle(e.target.value)}
					/>
				</div>
				<div className="documents-page__field">
					<label>Дата вступления в силу (текст)</label>
					<input
						type="text"
						value={effectiveDate}
						disabled={isLoading || isSaving}
						onChange={(e) => setEffectiveDate(e.target.value)}
					/>
				</div>
				<div className="documents-page__editor">
					<HtmlEditor
						value={bodyHtml}
						disabled={isLoading || isSaving}
						height={520}
						valueType="html"
						onValueChanged={(e) => setBodyHtml(e.value ?? "")}
					>
						<Toolbar multiline>
							<Item name="undo" />
							<Item name="redo" />
							<Item name="separator" />
							<Item name="header" acceptedValues={HEADER_VALUES} />
							<Item name="separator" />
							<Item name="bold" />
							<Item name="italic" />
							<Item name="underline" />
							<Item name="separator" />
							<Item name="orderedList" />
							<Item name="bulletList" />
							<Item name="separator" />
							<Item name="link" />
							<Item name="clear" />
						</Toolbar>
					</HtmlEditor>
				</div>
				<div className="documents-page__actions">
					<button
						type="button"
						disabled={isLoading || isSaving || !isDirty}
						onClick={handleSave}
					>
						{isSaving ? "Сохранение..." : "Сохранить правку"}
					</button>
					<button
						type="button"
						className="documents-page__publish"
						disabled={isLoading || isSaving || isDirty}
						onClick={handlePublish}
						title={isDirty ? "Сначала сохраните правку" : ""}
					>
						Опубликовать новую версию
					</button>
					{currentVersion != null && (
						<span className="documents-page__version">
							Текущая версия: {currentVersion}
						</span>
					)}
				</div>
				{status && (
					<p
						className={`documents-page__status ${
							status.ok ? "is-ok" : "is-error"
						}`}
					>
						{status.text}
					</p>
				)}
			</section>
			<section className="documents-page__card">
				<h3>История изменений</h3>
				<table className="documents-page__history">
					<thead>
						<tr>
							<th>Версия</th>
							<th>Язык</th>
							<th>Тип</th>
							<th>Дата (UTC)</th>
							<th>Админ</th>
							<th></th>
						</tr>
					</thead>
					<tbody>
						{history.map((h) => (
							<tr key={h.id}>
								<td>{h.version}</td>
								<td>{h.lang}</td>
								<td>
									{h.changeType === "publish"
										? "Публикация"
										: h.changeType === "rollback"
											? "Откат"
											: "Правка"}
								</td>
								<td>{new Date(h.changedAtUtc).toLocaleString()}</td>
								<td>{h.changedByAdminUserId ?? "—"}</td>
								<td>
									<button type="button" onClick={() => openEntry(h.id)}>
										Открыть
									</button>
									<button
										type="button"
										disabled={h.version === currentVersion}
										onClick={() => handleRollback(h.version)}
									>
										Откатить к этой версии
									</button>
								</td>
							</tr>
						))}
					</tbody>
				</table>
			</section>
			{viewEntry && (
				<section className="documents-page__card documents-page__viewer">
					<h3>
						Версия {viewEntry.version} ({viewEntry.lang}) — {viewEntry.title}
					</h3>
					<HtmlEditor value={viewEntry.bodyHtml} readOnly height={400} valueType="html" />
					<button type="button" onClick={() => setViewEntry(null)}>
						Закрыть
					</button>
				</section>
			)}
		</div>
	);
};

export default Documents;
